/**
 * Settings (design: hub + Account, Wallet, Notification Preferences, Subscription; split view on wide screens).
 * Every save uses the same calls as today's account page (src/legacy/pages/AccountPage.tsx):
 *  photo -> uploadAvatar + /api/customer/update-avatar; address -> today's EnvelopeModal; cards -> /api/payment-methods
 *  (+ today's AddPaymentMethodModal / PaymentMethodCard); gift code -> /api/wallet/redeem; email/SMS -> preferences;
 *  subscription management -> today's Subscription page, unchanged (plan change, cancel, reactivate, resubscribe).
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { uploadAvatar } from '@/services/uploadService'
import { prepareImageForUpload } from '@/utils/prepareImage'
import { useMailingAddress } from '@/features/mailing/useMailingAddress'
import { EnvelopeModal } from '@/features/mailing/EnvelopeModal'
import { getPaymentMethods, deletePaymentMethod, setDefaultPaymentMethod } from '@/api/paymentMethods'
import type { PaymentMethod } from '@/types/paymentMethod'
import PaymentMethodCard from '@/features/wallet/PaymentMethodCard'
import AddPaymentMethodModal from '@/features/wallet/AddPaymentMethodModal'
import { SubscriptionPage } from '@/pages/SubscriptionPage'
import { getIdentityStatus } from '@/api/identityVerification'
import { getDocumentStatus, type DocumentRecord } from '@/api/documents'
import { DocumentSigningModal } from '@/components/documents/DocumentSigningModal'
import {
  MASTER_AGREEMENT_TITLE, MASTER_AGREEMENT_TYPE, MASTER_AGREEMENT_VERSION, MASTER_AGREEMENT_SECTIONS,
  MEETUP_AGREEMENT_TITLE, MEETUP_AGREEMENT_TYPE, MEETUP_AGREEMENT_VERSION, MEETUP_AGREEMENT_SECTIONS,
} from '@/components/documents/agreementTexts'
import { FEATURES } from '~/config/mode'
import { SUPPORT_EMAIL } from '@/config/brandConfig'
import { api } from '~/api/client'
import { settings } from '~/api/endpoints'
import { Icon, displayName, useToast } from '~/app/ui'
import { signOutEverywhere } from '~/app/session'
import { nick } from '~/app/device'
import { hasPin, usePin } from '~/app/pin'

type Page = 'hub' | 'account' | 'wallet' | 'notifications' | 'subscription'
const PAGES: [Exclude<Page, 'hub'>, string, string][] = [
  ['account', 'i-user', 'Account Settings'], ['wallet', 'i-wallet', 'Wallet'],
  ['notifications', 'i-bell', 'Notification Preferences'], ['subscription', 'i-card', 'Subscription'],
]
const useWide = () => {
  const [w, setW] = useState(() => matchMedia('(min-width: 900px)').matches)
  useEffect(() => { const m = matchMedia('(min-width: 900px)'); const on = () => setW(m.matches); m.addEventListener('change', on); return () => m.removeEventListener('change', on) }, [])
  return w
}

export function Settings() {
  const { page = 'hub' } = useParams<{ page?: Page }>()
  const navigate = useNavigate()
  const wide = useWide()
  const current: Page = (['account', 'wallet', 'notifications', 'subscription'] as Page[]).includes(page as Page) ? (page as Page) : 'hub'
  const shown: Page = wide && current === 'hub' ? 'account' : current

  if (current === 'subscription' && new URLSearchParams(location.search).has('manage')) {
    // today's full subscription page (all its cases), inside the new frame
    return <main className="wrap" id="main"><div className="tw-scope"><SubscriptionPage /></div></main>
  }

  const hub = (
    <>
      <div className="set-head"><h1>Settings</h1></div>
      <ul className="set-hub">
        {PAGES.map(([k, icon, label]) => (
          <li key={k}><button className="set-row" type="button" aria-current={wide && shown === k ? 'true' : undefined} onClick={() => navigate(`/settings/${k}`)}>
            <Icon id={icon} /><span className="grow">{label}</span><Icon id="i-chev" className="icon chev" /></button></li>
        ))}
      </ul>
      <div className="set-foot">
        <button className="btn btn-sm" type="button" onClick={() => signOutEverywhere(navigate)}>Logout</button>
        <button className="link-btn" type="button" onClick={() => navigate('/home')}>← Back to Home</button>
      </div>
    </>
  )
  const panel = shown === 'account' ? <Account /> : shown === 'wallet' ? <Wallet /> : shown === 'notifications' ? <Notifications /> : shown === 'subscription' ? <Subscription /> : null

  return (
    <main className="wrap" id="main" aria-label="Settings">
      <div className={`set-wrap${wide ? ' split' : ''}`}>
        {wide ? <div className="set-split"><aside className="set-side">{hub}</aside><section className="set-panel">{panel}</section></div>
          : current === 'hub' ? hub : panel}
      </div>
    </main>
  )
}

function Head({ title }: { title: string }) {
  const navigate = useNavigate()
  return <div className="set-head"><button className="set-back" type="button" aria-label="Back to Settings" onClick={() => navigate('/settings')}><Icon id="i-back" /></button><h1>{title}</h1></div>
}

function Account() {
  const { user } = useAuth()
  const toast = useToast()
  const file = useRef<HTMLInputElement>(null)
  const [avatar, setAvatar] = useState<string | null>((user as any)?.avatar_url || null)
  const [busy, setBusy] = useState(false)
  const [envelope, setEnvelope] = useState(false)
  const { address, hasAddress, refresh } = useMailingAddress()
  const name = displayName(user)
  const { changePin } = usePin()
  const [nickSaved, setNickSaved] = useState(() => nick.get(user?.id))
  const [nickDraft, setNickDraft] = useState(nickSaved)

  async function pick(f: File | undefined) {
    if (!f || !user) return
    setBusy(true)
    try {
      const ready = await prepareImageForUpload(f)
      // the upload answers with the photo's STORAGE KEY (today's uploadService), not a viewable link - the server signs a
      // fresh link on sign-in / session check. So: show the picked photo right away, save the key, then ask for the
      // fresh signed link (owner 11-04: "photo is not uploading" = the bare key was shown as a broken image)
      const key = await uploadAvatar(ready, user.id)
      const r = await api('/api/customer/update-avatar', { method: 'POST', body: { userId: user.id, avatarUrl: key } })
      if (!r.ok) throw new Error(r.error || 'Could not save your photo')
      const local = URL.createObjectURL(ready)
      setAvatar(local); window.dispatchEvent(new CustomEvent('cw-avatar-changed', { detail: local })); toast('Photo updated')
      const fresh = await api<{ user?: { avatar_url?: string } }>('/api/auth/validate-token', { method: 'POST', body: { token: localStorage.getItem('auth_token') } })
      const signed = fresh.data?.user?.avatar_url
      if (signed && /^https?:/.test(signed)) {
        try { localStorage.setItem('user_data', JSON.stringify({ ...JSON.parse(localStorage.getItem('user_data') || '{}'), avatar_url: signed })) } catch { /* private mode */ }
        setAvatar(signed); window.dispatchEvent(new CustomEvent('cw-avatar-changed', { detail: signed }))
      }
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not upload that photo') } finally { setBusy(false) }
  }
  const field = (label: string, value: string) => (
    <div className="field"><label>{label}</label><input value={value} readOnly aria-readonly="true" /></div>
  )
  const a: any = address
  return (
    <>
      <Head title="Account Settings" />
      <div className="set-card">
        <div className="set-photo">
          {avatar ? <img className="big" src={avatar} alt="" style={{ objectFit: 'cover' }} /> : <span className="big" aria-hidden="true">{(name || 'C')[0]}</span>}
          <button className="btn btn-quiet" type="button" disabled={busy} onClick={() => file.current?.click()}>{busy ? 'Uploading…' : 'Upload Photo'}</button>
          <input ref={file} type="file" accept="image/*" hidden onChange={(e) => pick(e.target.files?.[0])} />
        </div>
        <h2>Profile Information</h2>
        <div className="two">{field('First Name', (user as any)?.firstName || (user as any)?.first_name || '')}{field('Last Name', (user as any)?.lastName || (user as any)?.last_name || '')}</div>
        {FEATURES.nickname && (
          <div className="field"><label htmlFor="nickEdit">Nickname</label>
            <div className="v2-row">
              <input id="nickEdit" maxLength={24} placeholder="Not set" value={nickDraft} onChange={(e) => setNickDraft(e.target.value)} />
              <button className="btn btn-sm" type="button" disabled={nickDraft.trim() === nickSaved} onClick={() => {
                if (!user) return
                nick.set(user.id, nickDraft.trim()); nick.markAsked(user.id); setNickSaved(nickDraft.trim())
                window.dispatchEvent(new Event('cw-nick-changed')); toast('Nickname saved on this device')
              }}>Save</button>
            </div>
            <p className="hint">Test version: kept on this device for now.</p>
          </div>
        )}
        {FEATURES.pinGate && (
          <div className="field"><label>PIN for your texts &amp; vault</label>
            <div className="v2-row"><p className="static" style={{ flex: 1 }}>{user && hasPin(user.id) ? 'Set on this device' : 'Not set yet'}</p>
              <button className="btn btn-quiet" type="button" onClick={changePin}>{user && hasPin(user.id) ? 'Change PIN' : 'Create PIN'}</button></div>
            <p className="hint">Saved to your account, so it works on every device. Forgot it? Sign out and back in with your email code.</p>
          </div>
        )}
        {field('Email', user?.email || '')}
        <div className="field"><label>Mailing Address</label>
          {hasAddress && a ? <p className="static">{[a.street_address || a.address_line1 || a.line1, a.city, a.state, a.zip_code || a.postal_code].filter(Boolean).join(', ')}</p> : <p className="static">No mailing address on file</p>}
          <button className="btn btn-quiet" type="button" onClick={() => setEnvelope(true)}>{hasAddress ? 'Change address' : '+ Add Address'}</button>
        </div>
        <Documents />
      </div>
      <div className="tw-scope"><EnvelopeModal isOpen={envelope} onClose={() => setEnvelope(false)} onAddressSaved={refresh} /></div>
    </>
  )
}

/**
 * Documents (today's Account page, kept as is): only for identity-verified fans - the NDA and the Meetup Agreement,
 * signed in today's DocumentSigningModal (GET /api/identity/status, GET /api/documents/status, POST /api/documents/sign).
 */
function Documents() {
  const [verified, setVerified] = useState(false)
  const [docs, setDocs] = useState<DocumentRecord[] | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const token = () => localStorage.getItem('auth_token') || ''
  const reload = async () => { try { setDocs((await getDocumentStatus(token())).documents) } catch { /* keep what we have */ } }
  useEffect(() => {
    let live = true
    Promise.all([getIdentityStatus(token()), getDocumentStatus(token())])
      .then(([i, d]) => { if (live) { setVerified(!!i.verified); setDocs(d.documents || []) } })
      .catch(() => { if (live) setDocs([]) })
    return () => { live = false }
  }, [])
  if (!verified || !docs) return null
  const signed = (t: string) => docs.some((d) => d.type === t)
  const row = (type: string, label: string) => (
    <button className="v2-doc" type="button" onClick={() => setOpen(type)}>
      <Icon id="i-pen" /><span className="grow">{label}</span>
      <span className={`v2-doc-state${signed(type) ? ' ok' : ''}`}>{signed(type) ? <><Icon id="i-check" />Signed</> : 'Sign now'}</span>
    </button>
  )
  return (
    <>
      <h2>Documents</h2>
      <div className="v2-docs">{row(MASTER_AGREEMENT_TYPE, 'NDA')}{row(MEETUP_AGREEMENT_TYPE, 'Meetup Agreement')}</div>
      <div className="tw-scope">
        <DocumentSigningModal isOpen={open === MASTER_AGREEMENT_TYPE} onClose={() => setOpen(null)} onSigned={reload}
          documentType={MASTER_AGREEMENT_TYPE} documentVersion={MASTER_AGREEMENT_VERSION} title={MASTER_AGREEMENT_TITLE} sections={MASTER_AGREEMENT_SECTIONS} />
        <DocumentSigningModal isOpen={open === MEETUP_AGREEMENT_TYPE} onClose={() => setOpen(null)} onSigned={reload}
          documentType={MEETUP_AGREEMENT_TYPE} documentVersion={MEETUP_AGREEMENT_VERSION} title={MEETUP_AGREEMENT_TITLE} sections={MEETUP_AGREEMENT_SECTIONS} />
      </div>
    </>
  )
}

function Wallet() {
  const toast = useToast()
  const [balance, setBalance] = useState<number | null>(null)
  const [cards, setCards] = useState<PaymentMethod[] | null>(null)
  const [adding, setAdding] = useState(false)
  const [code, setCode] = useState('')
  const [redeeming, setRedeeming] = useState(false)
  const token = () => localStorage.getItem('auth_token') || ''
  const loadCards = async () => { try { setCards((await getPaymentMethods(token())).payment_methods || []) } catch { setCards([]) } }
  useEffect(() => { settings.walletBalance().then(setBalance); loadCards() }, [])

  async function redeem() {
    if (!code.trim()) return
    setRedeeming(true)
    const r = await api<any>('/api/wallet/redeem', { method: 'POST', body: { code: code.trim() } })
    setRedeeming(false)
    if (r.ok) { setCode(''); toast('Code redeemed'); settings.walletBalance().then(setBalance) } else toast(r.error || 'That code didn’t work')
  }
  return (
    <>
      <Head title="Wallet / Gift card" />
      <div className="set-card">
        <h2>Balance</h2>
        <p className="static" style={{ fontSize: '1.5rem' }}>{balance === null ? '…' : `$${balance.toFixed(2)}`}</p>
        <div className="field"><label htmlFor="giftCode">Gift card or code</label>
          <div className="v2-row"><input id="giftCode" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter a code" autoComplete="off" />
            <button className="btn btn-sm" type="button" disabled={!code.trim() || redeeming} onClick={redeem}>{redeeming ? '…' : 'Redeem'}</button></div>
        </div>
        <h2>Payment methods</h2>
        {cards === null ? <p className="v2-muted">Loading…</p> : cards.length === 0 ? <p className="empty">No saved cards.</p> : (
          <ul className="v2-list tw-scope">
            {cards.map((c) => (
              <li key={c.id}><PaymentMethodCard paymentMethod={c}
                onSetDefault={async (id) => { await setDefaultPaymentMethod(token(), id); toast('Default card updated'); await loadCards() }}
                onDelete={async (id) => { await deletePaymentMethod(token(), id); toast('Card removed'); await loadCards() }} /></li>
            ))}
          </ul>
        )}
        <button className="btn btn-quiet" type="button" onClick={() => setAdding(true)}>+ Add payment method</button>
      </div>
      <div className="tw-scope"><AddPaymentMethodModal isOpen={adding} onClose={() => setAdding(false)} onSuccess={() => { setAdding(false); loadCards() }} /></div>
    </>
  )
}

function Notifications() {
  const { user } = useAuth()
  const toast = useToast()
  const [p, setP] = useState<{ email: boolean; sms: boolean } | null>(null)
  useEffect(() => { if (user?.id) settings.preferences(user.id).then(setP) }, [user?.id])
  async function flip(which: 'email' | 'sms') {
    if (!p || !user) return
    const next = { ...p, [which]: !p[which] }
    setP(next)
    const r = await settings.setPreference(user.id, which, next[which])
    if (!r.ok) { setP(p); toast('Couldn’t save that. Please try again.') }
  }
  const row = (k: 'email' | 'sms', title: string, desc: string) => (
    <div className="toggle-row"><span className="grow"><b>{title}</b><small>{desc}</small></span>
      <button className="switch" type="button" role="switch" aria-checked={!!p?.[k]} aria-label={title} disabled={!p} onClick={() => flip(k)} /></div>
  )
  return (
    <>
      <Head title="Notification Preferences" />
      <div className="set-card">
        <p className="sub">Choose how you want to be notified when you receive new messages</p>
        <div>{row('email', 'Email Notifications', 'Get notified via email when you receive messages')}{row('sms', 'SMS Notifications', 'Get notified via text message when you receive messages')}</div>
      </div>
    </>
  )
}

function Subscription() {
  const navigate = useNavigate()
  const [s, setS] = useState<any>(undefined)
  useEffect(() => { settings.subscription().then(setS) }, [])
  const status = s?.cancel_at_period_end ? 'Ends soon' : s?.status === 'trialing' ? 'Trial' : s?.status === 'active' ? 'Active' : s?.status === 'past_due' ? 'Payment due' : s ? s.status : null
  const plan = s ? `Full Private Access - ${s.plan_type === 'yearly' || s.plan_type === 'annual' ? 'Yearly' : 'Monthly'}` : 'No active subscription'
  const end = s?.current_period_end ? new Date(s.current_period_end).toLocaleDateString('en-US') : null
  return (
    <>
      <Head title="Subscription" />
      <div className="set-card">
        {s === undefined ? <p className="v2-muted">Loading…</p> : (
          <>
            {status && <span className="status">{status}</span>}
            <h2>{plan}</h2>
            {end && <p className="sub">{s?.cancel_at_period_end ? `Access until: ${end}` : `Next billing: ${end}`}</p>}
            <button className="btn btn-light full" type="button" onClick={() => navigate('/settings/subscription?manage')}>Manage Subscription</button>
          </>
        )}
      </div>
      <p className="v2-muted" style={{ marginTop: 12 }}>Billing questions? <a className="link-btn" href={`mailto:${SUPPORT_EMAIL}`}>Email support</a></p>
    </>
  )
}
